import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';

// Общие куски экранов аналитики v3: форматы, плитка показателя, графики.
// Графики — простые SVG/HTML без библиотек: столбики по дням с разбивкой по каналам,
// каскад «оборот → осталось магазину → прибыль», полосы, тепловая карта.

// Цвета каналов — палитра админки (хвойный, терракота, охра…), по сущности, в порядке стопки
// столбика (screens.py: telegram, web, max, площадки, manual, other). dataviz validate_palette.js
// на белой карточке — все проверки пройдены в этом порядке (15.10). Новый канал — запасной цвет.
export const CHANNEL_COLORS = {
  telegram: '#126F46',
  web: '#D77343',
  max: '#215D96',
  flowwow: '#B48C2B',
  flowwow_direct: '#A0446D',
  manual: '#6874BB',
  other: '#9B632D',
};
const SPARE = ['#9B632D', '#A0446D', '#6874BB'];
export function channelColor(key, i = 0) {
  return CHANNEL_COLORS[key] || SPARE[i % SPARE.length];
}

/** Состояние, которое помнится между вкладками и заходами (выбор владельца в этом браузере).
 *  Хранилище может быть недоступно (приватный режим) — тогда просто живём без памяти. */
export function usePersisted(key, initial) {
  const [v, setV] = useState(() => {
    try {
      const raw = window.localStorage.getItem(key);
      return raw != null ? JSON.parse(raw) : initial;
    } catch {
      return initial;
    }
  });
  useEffect(() => {
    try { window.localStorage.setItem(key, JSON.stringify(v)); } catch { /* без памяти */ }
  }, [key, v]);
  return [v, setV];
}

/** «Закончится»: понятный ответ вместо «хватит на 3,4 дн.» */
export function runOut(daysLeft, stock, noStockText) {
  if (noStockText) return { main: 'нет в наличии', sub: null };
  if (daysLeft == null) return { main: '—', sub: stock > 0 ? 'не продаётся' : null };
  if (daysLeft < 1) return { main: 'сегодня', sub: null };
  if (daysLeft < 2) return { main: 'завтра', sub: null };
  const d = new Date();
  d.setDate(d.getDate() + Math.floor(daysLeft));
  const days = Math.floor(daysLeft);
  const months = ['янв', 'фев', 'мар', 'апр', 'мая', 'июн', 'июл', 'авг', 'сен', 'окт', 'ноя', 'дек'];
  return { main: days > 60 ? 'нескоро' : `${d.getDate()} ${months[d.getMonth()]}`, sub: days > 60 ? null : `через ${plural(days, 'день', 'дня', 'дней')}` };
}

export const fmtRub = (v) => (v == null || Number.isNaN(v) ? '—' : `${Math.round(v).toLocaleString('ru-RU')} ₽`);
export const fmtNum = (v, d = 0) => (v == null || Number.isNaN(v) ? '—' : Number(v).toLocaleString('ru-RU', { maximumFractionDigits: d }));
export const fmtPct = (v, d = 1) => (v == null || Number.isNaN(v) ? '—' : `${fmtNum(v, d)} %`);

export function fmtValue(v, fmt) {
  if (v == null) return '—';
  if (fmt === 'rub') return fmtRub(v);
  if (fmt === 'pct') return fmtPct(v);
  if (fmt === 'days') return `${fmtNum(v, 1)} дн.`;
  return fmtNum(v);
}

/** Крупное значение: число шрифтом цифр, единица — основным (в Tektur «д» похожа на «g») */
export function BigValue({ v, fmt }) {
  if (v == null) return <>—</>;
  const unit = fmt === 'rub' ? '₽' : fmt === 'pct' ? '%' : fmt === 'days' ? 'дн.' : '';
  const num = (fmt === 'rub' ? Math.round(v).toLocaleString('ru-RU') : fmtNum(v, fmt === 'int' ? 0 : 1)).replace(/^-/, '−');
  return <>{num}{unit && <span className="an-unit">{'\u00a0'}{unit}</span>}</>;
}

/** «1 заказ, 2 заказа, 5 заказов» */
export function plural(n, one, few, many) {
  const m10 = n % 10; const m100 = n % 100;
  const w = m10 === 1 && m100 !== 11 ? one : (m10 >= 2 && m10 <= 4 && !(m100 >= 12 && m100 <= 14) ? few : many);
  return `${fmtNum(n)} ${w}`;
}

const HATCH = 'repeating-linear-gradient(135deg, #B0603C 0 3px, #FFE7DD 3px 6px)';

/** Блок экрана: карточка со срезом и персиковой шапкой */
export function Panel({ title, aside, children, className = '', bodyStyle }) {
  return (
    <section className={`card an-panel ${className}`}>
      {(title || aside) && (
        <div className="an-panel-head">
          {title && <h2 className="an-panel-title">{title}</h2>}
          {aside && <div className="an-panel-aside">{aside}</div>}
        </div>
      )}
      <div className="an-panel-body" style={bodyStyle}>{children}</div>
    </section>
  );
}

/** «?» с пояснением: открывается нажатием и с клавиатуры, не только наведением */
export function Hint({ text }) {
  const [pos, setPos] = useState(null);
  if (!text) return null;
  // Пояснение рисуется поверх страницы (portal + fixed): внутри таблицы с прокруткой оно обрезалось (13.10)
  const toggle = (e) => {
    if (pos) { setPos(null); return; }
    const r = e.currentTarget.getBoundingClientRect();
    const w = Math.min(280, window.innerWidth - 16);
    const left = Math.max(8, Math.min(r.left + r.width / 2 - w / 2, window.innerWidth - w - 8));
    const below = r.bottom + 8 + 160 < window.innerHeight;
    setPos({ left, width: w, ...(below ? { top: r.bottom + 6 } : { bottom: window.innerHeight - r.top + 6 }) });
  };
  return (
    <span className="an-hint-wrap">
      <button type="button" className="an-hint" aria-label="Пояснение" aria-expanded={!!pos}
        onClick={toggle} onBlur={() => setPos(null)}>?</button>
      {pos && createPortal(<span className="an-hint-pop an-hint-fixed" role="tooltip" style={pos}>{text}</span>, document.body)}
    </span>
  );
}

/** Плавная линия через точки без «выбросов» между ними (монотонная кубическая, как d3 curveMonotoneX):
 * кривая не поднимается выше и не опускается ниже соседних значений — сглаживает вид, не данные */
export function smoothPath(pts) {
  const n = pts.length;
  if (n < 2) return '';
  const f = (v) => Math.round(v * 100) / 100;
  if (n === 2) return `M${f(pts[0][0])},${f(pts[0][1])}L${f(pts[1][0])},${f(pts[1][1])}`;
  const dx = []; const m = [];
  for (let i = 0; i < n - 1; i++) {
    dx[i] = pts[i + 1][0] - pts[i][0];
    m[i] = dx[i] ? (pts[i + 1][1] - pts[i][1]) / dx[i] : 0;
  }
  const t = [m[0]];
  for (let i = 1; i < n - 1; i++) {
    if (m[i - 1] * m[i] <= 0) t[i] = 0;
    else {
      const w1 = 2 * dx[i] + dx[i - 1]; const w2 = dx[i] + 2 * dx[i - 1];
      t[i] = (w1 + w2) / (w1 / m[i - 1] + w2 / m[i]);
    }
  }
  t[n - 1] = m[n - 2];
  let d = `M${f(pts[0][0])},${f(pts[0][1])}`;
  for (let i = 0; i < n - 1; i++) {
    const h = dx[i] / 3;
    d += `C${f(pts[i][0] + h)},${f(pts[i][1] + t[i] * h)} ${f(pts[i + 1][0] - h)},${f(pts[i + 1][1] - t[i + 1] * h)} ${f(pts[i + 1][0])},${f(pts[i + 1][1])}`;
  }
  return d;
}

export function Sparkline({ values, width = 112, height = 32, color = '#357156' }) {
  if (!values || values.length < 2) return null;
  const mx = Math.max(...values);
  const mn = Math.min(...values);
  const span = mx - mn || 1;
  const pts = values.map((v, i) => [i * width / (values.length - 1), height - 3 - (v - mn) / span * (height - 6)]);
  const line = smoothPath(pts);
  return (
    <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} aria-hidden="true" className="an-spark">
      <path d={`${line}L${width},${height}L0,${height}Z`} fill={color} opacity="0.1" />
      <path d={line} fill="none" stroke={color} strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
    </svg>
  );
}

export function Delta({ value, unit = '%', good = 'up', note }) {
  if (value == null) return null;
  const isGood = good === 'up' ? value >= 0 : value <= 0;
  const sign = value > 0 ? '+' : value < 0 ? '−' : '±';
  const u = unit === '%' ? ' %' : unit ? ` ${unit}` : '';
  return (
    <span className="an-delta">
      <b style={{ color: value === 0 ? '#5E6561' : isGood ? '#1F763B' : '#A33A33' }}>{sign}{fmtNum(Math.abs(value), 1)}{u}</b>
      {note && <span className="an-muted"> {note}</span>}
    </span>
  );
}

// Статус по порогу (services/analytics/thresholds.py): иконка + слово + причина — цвет не
// единственный носитель смысла. Пороги — значения по умолчанию; правку в настройках убрали (владелец 25.10).
export const STATUS = {
  warning: { word: 'Внимание', cls: 'an-st-warning' },
  critical: { word: 'Плохо', cls: 'an-st-critical' },
};

export function StatusIcon({ status, size = 14 }) {
  if (status === 'critical') {
    return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M12 3l9.5 17h-19z" /><path d="M12 10v4" /><path d="M12 17.5v.5" /></svg>;
  }
  return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="9" /><path d="M12 7.5v5" /><path d="M12 16v.5" /></svg>;
}

/** Метка статуса: «Плохо» / «Внимание» с иконкой; text — почему (в подсказке или рядом) */
export function StatusTag({ status, text, inline = false }) {
  if (!status) return null;
  const s = STATUS[status];
  return (
    <span className={`an-status ${s.cls}`} title={text || s.word} style={{ '--ch': '4px' }}>
      <StatusIcon status={status} size={12} />{s.word}{inline && text ? <span className="an-status-text"> · {text}</span> : null}
    </span>
  );
}

/** Плитка показателя: значение, пояснение, сравнение, тренд. За порогом — полоса сверху и строка
 *  «Плохо/Внимание: почему» (без заливки всей плитки — иначе «ёлка», ревью UX 12.10) */
export function KpiTile({ tile, compare = true }) {
  const st = tile.status ? STATUS[tile.status] : null;
  const showDelta = compare && tile.delta != null && !tile.delta_hidden;
  return (
    <div className={`card an-tile${tile.hero ? ' an-tile-hero' : ''}${st ? ` ${st.cls}` : ''}`}>
      {st && <span className="an-tile-stripe" aria-hidden="true" />}
      <div className="an-tile-top">
        <span>{tile.title} <Hint text={tile.hint} /></span>
        {tile.est && <span className="an-chip-est" style={{ '--ch': '5px', '--stroke': '#E4C177' }} title="Часть суммы посчитана по ставке — площадка ещё не прислала свои цифры">оценка</span>}
      </div>
      <div className="an-tile-value"><BigValue v={tile.value} fmt={tile.fmt} /></div>
      {tile.sub && <div className="an-muted an-tile-sub">{tile.sub}</div>}
      {st && (
        <div className="an-tile-status">
          <StatusIcon status={tile.status} size={13} /><span><b>{st.word}:</b> {tile.status_text}</span>
        </div>
      )}
      {(showDelta || (tile.spark && tile.spark.length > 1)) && (
        <div className="an-tile-bottom">
          {showDelta ? <Delta value={tile.delta} unit={tile.delta_unit} good={tile.good} note={tile.delta_note || 'к прошлому'} /> : <span />}
          <Sparkline values={tile.spark} />
        </div>
      )}
    </div>
  );
}

/** Ряд ключевых показателей дашборда — одинаковые плитки, без «героя» */
export function KpiRow({ tiles, keys, compare = true }) {
  const by = Object.fromEntries((tiles || []).map(t => [t.key, t]));
  const list = (keys ? keys.map(k => by[k]) : (tiles || [])).filter(Boolean);
  if (!list.length) return null;
  return (
    <div className="an-kpis" data-n={list.length}>
      {list.map(t => <KpiTile key={t.key} tile={{ ...t, hero: false }} compare={compare} />)}
    </div>
  );
}

/** Заголовок колонки с сортировкой по нажатию (все таблицы аналитики, UX 15.10): первое нажатие на число —
 *  по убыванию, на название — по алфавиту; стрелка только у активной колонки */
export function SortTh({ k, sort, setSort, className = 'r', children, hint }) {
  const active = sort.key === k;
  const first = k === 'name' ? 'asc' : 'desc';
  return (
    <th className={`${className} an-th-sort`} aria-sort={active ? (sort.dir === 'asc' ? 'ascending' : 'descending') : 'none'}>
      <button type="button" onClick={() => setSort(s => ({ key: k, dir: s.key === k ? (s.dir === 'desc' ? 'asc' : 'desc') : first }))}>
        {children}{active ? (sort.dir === 'desc' ? ' ↓' : ' ↑') : ''}
      </button>
      {hint && <Hint text={hint} />}
    </th>
  );
}

export function sortRows(rows, sort, sorts) {
  const s = sorts[sort.key] || Object.values(sorts)[0];
  const sign = sort.dir === 'asc' ? 1 : -1;
  return [...rows].sort((a, b) => {
    const va = s.get(a); const vb = s.get(b);
    if (typeof va === 'string') return sign * va.localeCompare(vb, 'ru');
    return sign * ((va > vb) - (va < vb));
  });
}

/** Одна строка-легенда статусов на экран */
export function StatusLegend() {
  return (
    <div className="an-status-legend an-muted an-small">
      <span className="an-st-critical"><StatusIcon status="critical" size={12} /> Плохо</span>
      <span className="an-st-warning"><StatusIcon status="warning" size={12} /> Внимание</span>
      <span>— показатель за порогом</span>
    </div>
  );
}

function niceStep(max) {
  // мелкие шаги — для штук (люди по дням, 27.10); у денег max больше, они не выбираются
  const steps = [1, 2, 5, 10, 20, 25, 50, 100, 200, 250, 500, 1000, 2000, 5000, 10000, 20000, 25000, 50000, 100000, 200000, 250000, 500000, 1000000, 2000000, 5000000];
  for (const s of steps) if (max / s <= 4) return s;
  return steps[steps.length - 1];
}
// Компактно для подписей графиков: 850 · 8,5К · 145К · 1,2М (просьба владельца 13.10)
export const short = (v) => {
  const a = Math.abs(v);
  if (a >= 1000000) return `${fmtNum(v / 1000000, a >= 10000000 ? 0 : 1)}М`;
  if (a >= 1000) return `${fmtNum(v / 1000, a >= 10000 ? 0 : 1)}К`;
  return fmtNum(v);
};
/** Какие столбики/точки подписывать: все, если их до 14; иначе на телефоне — только максимум и последний */
const valKeep = (buckets, i) => {
  const mx = Math.max(...buckets.map(b => b.total));
  return buckets[i].total === mx || i === buckets.length - 1;
};

/** Столбики по дням/неделям с разбивкой по каналам. Наведение — разбивка справа.
 *  fmt / unit — для штук (люди по дням, 27.10): unit=null — без «N заказов» в итоге */
export function StackedBars({ buckets, channels, height = 240, fmt = fmtRub, unit = ['заказ', 'заказа', 'заказов'] }) {
  const [hover, setHover] = useState(null);
  const max = Math.max(1, ...buckets.map(b => b.total));
  const step = niceStep(max);
  const top = Math.ceil(max / step) * step;
  const ticks = [];
  for (let v = 0; v <= top; v += step) ticks.push(v);
  const many = buckets.length > 20;
  const labelEvery = buckets.length > 40 ? 7 : buckets.length > 20 ? 5 : 1;
  const cur = hover != null ? buckets[hover] : null;
  const totals = useMemo(() => {
    const t = {};
    for (const b of buckets) for (const [k, v] of Object.entries(b.values)) t[k] = (t[k] || 0) + v;
    return t;
  }, [buckets]);
  const vals = cur ? cur.values : totals;
  const total = cur ? cur.total : buckets.reduce((s, b) => s + b.total, 0);
  const orders = cur ? cur.orders : buckets.reduce((s, b) => s + b.orders, 0);
  return (
    <div className="an-bars-wrap">
      <div className="an-bars-plot" onMouseLeave={() => setHover(null)}>
        <div className="an-bars-area" style={{ height }}>
          {ticks.map(v => (
            <div key={v} className="an-bars-tick" style={{ bottom: `${(v / top) * 100}%`, borderColor: v === 0 ? '#A9B1AC' : '#EBEDEC' }}>
              <span>{v === 0 ? '0' : short(v)}</span>
            </div>
          ))}
          <div className="an-bars-cols" style={{ gap: many ? 3 : 10 }}>
            {buckets.map((b, i) => (
              <div key={b.key} className="an-bars-col" onMouseEnter={() => setHover(i)}
                onClick={() => setHover(h => (h === i ? null : i))} onFocus={() => setHover(i)}
                tabIndex={0} role="button" aria-label={`${b.full}: ${fmt(b.total)}${unit ? `, ${plural(b.orders, ...unit)}` : ''}`}
                data-on={hover === i ? '1' : undefined}>
                {b.total > 0 && <span className={`an-bar-val${buckets.length > 14 ? ' an-val-dense' : ''}${valKeep(buckets, i) ? ' keep' : ''}`}>{short(b.total)}</span>}
                <div className={`an-bars-stack${b.partial ? ' an-partial' : ''}`} style={{ height: `${(b.total / top) * 100}%` }}
                  title={b.partial ? 'День ещё идёт' : undefined}>
                  {channels.map((c, j) => {
                    const v = b.values[c.key] || 0;
                    if (!v) return null;
                    const last = channels.slice(j + 1).every(cc => !(b.values[cc.key] > 0));
                    return <div key={c.key} style={{ flex: `${v} 1 0`, background: channelColor(c.key, j), borderRadius: last ? '4px 4px 0 0' : 0 }} />;
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="an-bars-labels" style={{ gap: many ? 3 : 10 }}>
          {buckets.map((b, i) => (
            <div key={b.key}>{(buckets.length - 1 - i) % labelEvery === 0 ? (b.partial && buckets.length <= 14 ? 'сегодня' : b.label) : ''}</div>
          ))}
        </div>
      </div>
      <div className="an-readout">
        <div className="an-muted an-readout-hint">{cur ? 'Выбранный день · нажмите ещё раз, чтобы снять' : 'Весь период'}</div>
        <div className="an-readout-title">{cur ? cur.full : 'Сумма за период'}{cur && cur.partial ? ' · день ещё идёт' : ''}</div>
        {channels.map((c, j) => (
          <div key={c.key} className="an-readout-row">
            <span className="an-dot" style={{ background: channelColor(c.key, j) }} />
            <span className="an-grow">{c.title}</span>
            <b className="an-nowrap">{fmt(vals[c.key] || 0)}</b>
          </div>
        ))}
        <div className="an-readout-total">
          <span>Всего{unit ? ` · ${plural(orders, ...unit)}` : ''}</span>
          <b className="an-nowrap">{fmt(total)}</b>
        </div>
      </div>
    </div>
  );
}

/** Линия оборота по дням и пунктиром — прошлый период той же длины. Наведение / нажатие — значения справа */
export function LineChart({ buckets, height = 240, compareLabel = 'прошлый период', showPrev = true }) {
  const [hover, setHover] = useState(null);
  const n = buckets.length;
  const prevOk = showPrev && buckets.some(b => b.prev_total != null);
  const max = Math.max(1, ...buckets.map(b => b.total), ...(prevOk ? buckets.map(b => b.prev_total || 0) : []));
  const step = niceStep(max);
  const top = Math.ceil(max / step) * step;
  const ticks = [];
  for (let v = 0; v <= top; v += step) ticks.push(v);
  const X = (i) => (n === 1 ? 50 : (i / (n - 1)) * 100);
  const Y = (v) => 100 - (v / top) * 100;
  const path = (arr) => smoothPath(arr.map((v, i) => [X(i), Y(v)]));
  const partial = n > 1 && buckets[n - 1].partial;
  const solid = partial ? buckets.slice(0, n - 1).map(b => b.total) : buckets.map(b => b.total);
  const labelEvery = n > 40 ? 7 : n > 20 ? 5 : 1;
  const cur = hover != null ? buckets[hover] : null;
  const sum = buckets.reduce((a, b) => a + b.total, 0);
  const sumPrev = buckets.reduce((a, b) => a + (b.prev_total || 0), 0);
  return (
    <div className="an-bars-wrap">
      <div className="an-bars-plot" onMouseLeave={() => setHover(null)}>
        <div className="an-bars-area" style={{ height }}>
          {ticks.map(v => (
            <div key={v} className="an-bars-tick" style={{ bottom: `${(v / top) * 100}%`, borderColor: v === 0 ? '#A9B1AC' : '#EBEDEC' }}>
              <span>{v === 0 ? '0' : short(v)}</span>
            </div>
          ))}
          <svg className="an-line-svg" viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden="true">
            {prevOk && <path d={path(buckets.map(b => b.prev_total || 0))} fill="none" stroke="#A9B1AC" strokeWidth="2" strokeDasharray="5 4" vectorEffect="non-scaling-stroke" />}
            {solid.length > 1 && <path d={path(solid)} fill="none" stroke="#1C3D2E" strokeWidth="2.5" vectorEffect="non-scaling-stroke" strokeLinejoin="round" />}
            {partial && <polyline points={`${X(n - 2)},${Y(buckets[n - 2].total)} ${X(n - 1)},${Y(buckets[n - 1].total)}`}
              fill="none" stroke="#1C3D2E" strokeWidth="2.5" strokeDasharray="3 4" vectorEffect="non-scaling-stroke" />}
            {cur && <line x1={X(hover)} x2={X(hover)} y1="0" y2="100" stroke="#C1C8C4" strokeWidth="1" vectorEffect="non-scaling-stroke" />}
          </svg>
          {buckets.map((b, i) => (
            <span key={b.key} className={`an-line-dot${b.partial ? ' an-partial-dot' : ''}${hover === i ? ' on' : ''}`}
              style={{ left: `${X(i)}%`, bottom: `${100 - Y(b.total)}%` }} />
          ))}
          {buckets.map((b, i) => (b.total > 0 ? (
            <span key={`v${b.key}`} className={`an-line-val${n > 14 ? ' an-val-dense' : ''}${valKeep(buckets, i) ? ' keep' : ''}`}
              style={{ left: `${X(i)}%`, bottom: `${100 - Y(b.total)}%` }}>{short(b.total)}</span>
          ) : null))}
          <div className="an-bars-cols" style={{ gap: 0 }}>
            {buckets.map((b, i) => (
              <div key={b.key} className="an-bars-col" style={{ opacity: 1 }} onMouseEnter={() => setHover(i)}
                onClick={() => setHover(h => (h === i ? null : i))} onFocus={() => setHover(i)} tabIndex={0} role="button"
                aria-label={`${b.full}: ${fmtRub(b.total)}${b.prev_total != null ? `, ${compareLabel}: ${fmtRub(b.prev_total)}` : ''}`} />
            ))}
          </div>
        </div>
        <div className="an-bars-labels" style={{ gap: 0 }}>
          {buckets.map((b, i) => (
            <div key={b.key}>{(n - 1 - i) % labelEvery === 0 ? (b.partial && n <= 14 ? 'сегодня' : b.label) : ''}</div>
          ))}
        </div>
      </div>
      <div className="an-readout">
        <div className="an-muted an-readout-hint">{cur ? 'Выбранный день · нажмите ещё раз, чтобы снять' : 'Весь период'}</div>
        <div className="an-readout-title">{cur ? cur.full : 'Сумма за период'}{cur && cur.partial ? ' · день ещё идёт' : ''}</div>
        <div className="an-readout-row"><span className="an-legend-line" /><span className="an-grow">Оборот</span><b className="an-nowrap">{fmtRub(cur ? cur.total : sum)}</b></div>
        {prevOk && (
          <div className="an-readout-row"><span className="an-legend-line an-dashed" /><span className="an-grow">{cur && cur.prev_label ? cur.prev_label : compareLabel}</span>
            <b className="an-nowrap">{fmtRub(cur ? cur.prev_total : sumPrev)}</b></div>
        )}
        {prevOk && (
          <div className="an-readout-total">
            <span>Разница</span>
            <Delta value={(() => { const a = cur ? cur.total : sum; const b = cur ? cur.prev_total : sumPrev; return b ? Math.round((a - b) / b * 1000) / 10 : null; })()} />
          </div>
        )}
      </div>
    </div>
  );
}

export function Legend({ items }) {
  return (
    <div className="an-legend">
      {items.map((c, j) => (
        <span key={c.key} className="an-legend-item">
          <span className="an-dot" style={{ background: c.color || channelColor(c.key, j) }} />
          <span>{c.title}</span>
          {c.extra != null && <span className="an-muted">{c.extra}</span>}
        </span>
      ))}
    </div>
  );
}

/** Каскад денег: итоги — тёмно-зелёные, вычеты — терракотовые, прибавка (инвентаризация в плюс) —
 *  светло-зелёная со знаком «+», оценка — штриховкой */
export function Waterfall({ rows }) {
  const base = rows.length ? Math.max(...rows.filter(r => r.kind === 'total').map(r => Math.abs(r.value)), 1) : 1;
  let run = 0;
  const info = rows.filter(r => r.kind === 'info');
  return (
    <div className="an-wf">
      {rows.filter(r => r.kind !== 'info').map((r) => {
        let left; let width;
        if (r.kind === 'total') {
          run = r.value;
          left = 0;
          width = (Math.max(r.value, 0) / base) * 100;
        } else if (r.kind === 'plus') {
          const v = Math.abs(r.value);
          left = (Math.max(run, 0) / base) * 100;
          width = (v / base) * 100;
          run += v;
        } else {
          const v = Math.abs(r.value);
          run -= v;
          left = (Math.max(run, 0) / base) * 100;
          width = (v / base) * 100;
        }
        const est = r.kind === 'minus' && r.estimate ? (r.estimate / Math.abs(r.value)) * width : 0;
        const st = r.status ? STATUS[r.status] : null;
        return (
          <div key={r.key} className="an-wf-row">
            <span className={`an-wf-label${r.kind === 'total' ? ' an-b' : ''}${st ? ` ${st.cls} an-wf-flag` : ''}`} title={r.status_text || undefined}>
              {st && <StatusIcon status={r.status} size={12} />}{r.label}
              {r.note && <span className="an-wf-note">{r.note}{st ? ` · ${st.word.toLowerCase()}` : ''}</span>}
            </span>
            <div className="an-wf-track">
              <div style={{ left: `${left}%`, width: `${Math.max(width, 0.3)}%`, background: r.kind === 'total' ? '#1C3D2E' : r.kind === 'plus' ? '#4A8C6E' : '#B0603C' }} />
              {est > 0 && <div style={{ left: `${left}%`, width: `${est}%`, background: HATCH }} />}
            </div>
            {/* Итог может уйти в минус (операционная прибыль при больших затратах) — со знаком и красным */}
            <span className={`an-wf-value${r.kind === 'total' ? ' an-b' : ''}${r.kind === 'total' && r.value < 0 ? ' an-bad' : ''}`}>
              {r.kind === 'minus' || (r.kind === 'total' && r.value < 0) ? '−' : r.kind === 'plus' ? '+' : ''}{fmtRub(Math.abs(r.value))}</span>
          </div>
        );
      })}
      {info.map(r => (
        <div key={r.key} className="an-wf-info"><b>+ {r.label}: {fmtRub(r.value)}</b> <span className="an-muted">{r.note}</span></div>
      ))}
      <div className="an-legend an-muted" style={{ paddingTop: 6 }}>
        <span className="an-legend-item"><span className="an-dot" style={{ background: '#1C3D2E' }} />итог</span>
        <span className="an-legend-item"><span className="an-dot" style={{ background: '#B0603C' }} />вычет</span>
        {rows.some(r => r.kind === 'plus') && <span className="an-legend-item"><span className="an-dot" style={{ background: '#4A8C6E' }} />прибавка</span>}
        <span className="an-legend-item"><span className="an-dot" style={{ background: HATCH }} />оценка — площадка ещё не прислала свои цифры</span>
      </div>
    </div>
  );
}

/** Горизонтальные полосы одного ряда с подписью значения */
export function BarList({ items, color = '#357156' }) {
  const mx = Math.max(1, ...items.map(i => i.value || 0));
  return (
    <div className="an-barlist">
      {items.map((it) => (
        <div key={it.key || it.label} className="an-barlist-row">
          <span className="an-barlist-label">{it.label}</span>
          <div className="an-barlist-track"><div style={{ width: `${Math.max(1, (it.value || 0) / mx * 100)}%`, background: it.color || color }} /></div>
          <span className="an-barlist-value">{it.text}</span>
        </div>
      ))}
    </div>
  );
}

const RAMP = ['#F4F4EF', '#E6F2EA', '#B9DCC6', '#7FBF9A', '#3F8C64', '#1C4F36'];
export function rampColor(v, max) {
  if (!v) return RAMP[0];
  const i = Math.min(5, 1 + Math.floor((v / Math.max(max, 1)) * 4.999));
  return RAMP[i];
}

/** Переключатель в фирменной геометрии: срезаны углы у всей группы и у выбранного элемента
 *  (владелец 14.10). Срез и контур рисует общий механизм по инлайн-переменной --ch (styles.css) */
// Переключатель — общий .seg, как во всей админке: срез у контейнера, середина прямая (владелец 25.10)
export function Segmented({ options, value, onChange, small }) {
  return (
    <div className={`seg${small ? '' : ' seg-lg'}`} role="group">
      {options.map(o => (
        <button key={o.value} type="button" aria-pressed={value === o.value}
          className={`seg-btn${value === o.value ? ' on' : ''}`}
          onClick={() => onChange(o.value)}>{o.label}</button>
      ))}
    </div>
  );
}

export function Empty({ children = 'Нет данных за период' }) {
  return <div className="an-empty an-muted">{children}</div>;
}
